import React, { useState } from 'react';
import { LifebuoyIcon, XMarkIcon, DocumentTextIcon, EnvelopeIcon, BookOpenIcon } from '@heroicons/react/24/outline';

interface SupportManagerProps {
  variant?: 'sidebar' | 'header';
}

const SupportManager: React.FC<SupportManagerProps> = ({ variant = 'sidebar' }) => {
  const [showSupportModal, setShowSupportModal] = useState(false);

  const supportItems = [
    {
      id: 'docs',
      name: 'Docs',
      description: 'Complete documentation and guides for TrendBeat.',
      icon: DocumentTextIcon,
      href: '/docs',
      color: 'bg-blue-100 text-blue-600',
    },
    {
      id: 'contact',
      name: 'Contact Support',
      description: 'Get help from our support team.',
      icon: EnvelopeIcon,
      href: '/contact',
      color: 'bg-green-100 text-green-600',
    },
    {
      id: 'blog',
      name: 'Blog',
      description: 'Trend analysis strategies & insights.',
      icon: BookOpenIcon,
      href: '/blog',
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  // Sidebar variant - simple text with question mark icon
  if (variant === 'sidebar') {
    return (
      <>
        <button
          onClick={() => setShowSupportModal(!showSupportModal)}
          className="flex items-center justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md group"
        >
          <div className="flex items-center">
            <LifebuoyIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500" />
            <span>Support</span>
          </div>
        </button>

        {/* Support Modal */}
        {showSupportModal && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black bg-opacity-25 transition-opacity"
              onClick={() => setShowSupportModal(false)}
            />
            
            {/* Desktop Panel */}
            <div className="absolute bottom-0 left-0 mb-16 ml-64 w-80 bg-white rounded-lg shadow-xl border border-gray-200 hidden lg:block">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Support</h2>
                  <button
                    onClick={() => setShowSupportModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Support Items */}
                <div className="flex-1 overflow-y-auto max-h-96 p-4">
                  <div className="space-y-3">
                    {supportItems.map((item) => (
                      <a
                        key={item.id}
                        href={item.href}
                        className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                        onClick={() => setShowSupportModal(false)}
                      >
                        <div className={`p-2 rounded-lg ${item.color} mr-3 flex-shrink-0`}>
                          <item.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600 transition-colors duration-200">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {item.description}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Panel */}
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-xl border border-gray-200 lg:hidden">
              <div className="flex flex-col h-full max-h-96">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Support</h2>
                  <button
                    onClick={() => setShowSupportModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Support Items */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {supportItems.map((item) => (
                      <a
                        key={item.id}
                        href={item.href}
                        className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                        onClick={() => setShowSupportModal(false)}
                      >
                        <div className={`p-2 rounded-lg ${item.color} mr-3 flex-shrink-0`}>
                          <item.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600 transition-colors duration-200">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {item.description}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Header variant - question mark icon
  return (
    <>
      <button
        onClick={() => setShowSupportModal(!showSupportModal)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        title="Support"
      >
        <LifebuoyIcon className="h-6 w-6" />
      </button>

      {/* Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-25 transition-opacity"
            onClick={() => setShowSupportModal(false)}
          />
          
          {/* Desktop Panel */}
          <div className="absolute bottom-0 left-0 mb-16 ml-64 w-80 bg-white rounded-lg shadow-xl border border-gray-200 hidden lg:block">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Support</h2>
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Support Items */}
              <div className="flex-1 overflow-y-auto max-h-96 p-4">
                <div className="space-y-3">
                  {supportItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.href}
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                      onClick={() => setShowSupportModal(false)}
                    >
                      <div className={`p-2 rounded-lg ${item.color} mr-3 flex-shrink-0`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600 transition-colors duration-200">
                          {item.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.description}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-xl border border-gray-200 lg:hidden">
            <div className="flex flex-col h-full max-h-96">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Support</h2>
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Support Items */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {supportItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.href}
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                      onClick={() => setShowSupportModal(false)}
                    >
                      <div className={`p-2 rounded-lg ${item.color} mr-3 flex-shrink-0`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600 transition-colors duration-200">
                          {item.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.description}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportManager;
